import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { num, pct } from "../lib/format";
import { Icon } from "../components/icons";
import { Meter, Empty } from "../components/ui";
import type { Playbook, PlaybookRun, PlaybookStep, PlaybookStepKind, PlaybookScope, Assignee } from "../lib/types";
import { scheduleRunSteps, planDuration } from "../lib/schedule";
import { Gantt } from "../components/Gantt";
import * as work from "../lib/workClient";

const STEP_KINDS: PlaybookStepKind[] = ["task", "meeting", "email", "alert", "trigger", "wait", "branch"];
const isActionableKind = (k: string) => k === "task" || k === "email" || k === "meeting" || k === "alert";
/** A synthetic run so the Gantt can preview a template's schedule. */
function previewRun(pb: Playbook, start = "2026-09-01"): PlaybookRun {
  const steps = scheduleRunSteps(pb.steps, start, (t) => t, isActionableKind).map((s) => ({ ...s, taskId: undefined }));
  return { id: "preview", playbookId: pb.id, playbookName: pb.name, accountId: "", accountName: "", status: "active", steps, startedAt: `${start}T00:00:00Z`, planStartDate: start };
}

type CategoryFilter = Playbook["category"] | "all";
const categories: CategoryFilter[] = ["all", "Risk", "Onboarding", "Adoption", "Expansion", "Renewal"];
const stepIcon: Record<string, string> = { trigger: "bolt", task: "check", email: "mail", meeting: "calendar", wait: "clock", branch: "route", alert: "bell" };
const isActionable = (k: string) => k === "task" || k === "email" || k === "meeting" || k === "alert";
const rateColor = (r: number) => (r >= 70 ? "var(--good)" : r >= 50 ? "var(--watch)" : "var(--risk)");

export default function Playbooks() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [playbooks, setPlaybooks] = useState<Playbook[]>(ws.playbooks);
  const [runs, setRuns] = useState<PlaybookRun[]>(ws.playbookRuns);
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [selectedId, setSelectedId] = useState<string>(ws.playbooks[0]?.id ?? "");
  const [launchAcct, setLaunchAcct] = useState<string>(ws.accounts[0]?.id ?? "");
  const [toast, setToast] = useState("");
  const [designing, setDesigning] = useState<Playbook | "new" | null>(null);

  useEffect(() => { work.loadPlaybookRuns(ws).then((r) => { setMode(r.mode); setRuns(r.runs); setPlaybooks(r.playbooks); }); }, [ws]);
  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(""), 2600); };

  const filtered = useMemo(() => playbooks.filter((p) => category === "all" || p.category === category), [playbooks, category]);
  const selected = filtered.find((p) => p.id === selectedId) ?? filtered[0];
  const activeRuns = runs.filter((r) => r.status === "active");

  const toggle = async (p: Playbook) => {
    setPlaybooks((prev) => prev.map((x) => (x.id === p.id ? { ...x, enabled: !x.enabled } : x)));
    await work.togglePlaybook(mode, ws, p.id, !p.enabled);
  };
  const launch = async () => {
    if (!selected) return;
    const acct = ws.accounts.find((a) => a.id === launchAcct);
    const run = await work.launchPlaybook(mode, ws, selected.id, { accountId: launchAcct, scopeKind: "account", scopeId: launchAcct, scopeName: acct?.name, planStartDate: "2026-09-01" });
    if (run) { setRuns((prev) => [run, ...prev]); setPlaybooks((prev) => prev.map((x) => (x.id === selected.id ? { ...x, activeRuns: x.activeRuns + 1 } : x))); flash(`Applied "${selected.name}" on ${run.accountName} — ${run.steps.filter((s) => s.status === "pending").length} tasks scheduled`); }
  };
  const refreshPlaybooks = async () => { const r = await work.loadPlaybooks(ws); setPlaybooks(r.playbooks); };
  const saveDesign = async (input: Partial<Playbook> & { name: string }) => {
    if (designing && designing !== "new") await work.updatePlaybookTemplate(mode, ws, designing.id, input);
    else await work.createPlaybook(mode, ws, input);
    setDesigning(null); flash("Playbook saved"); refreshPlaybooks();
  };
  const removePlaybook = async (p: Playbook) => { await work.deletePlaybookTemplate(mode, ws, p.id); setDesigning(null); flash("Playbook deleted"); refreshPlaybooks(); };
  const advance = async (run: PlaybookRun) => { const r = await work.advanceRun(mode, ws, run.id); if (r) setRuns((prev) => prev.map((x) => (x.id === r.id ? r : x))); };
  const stop = async (run: PlaybookRun) => { await work.stopRun(mode, ws, run.id); setRuns((prev) => prev.map((x) => (x.id === run.id ? { ...x, status: "stopped" } : x))); };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Playbooks</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Signals into action</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Design a play with dependencies, durations, and owners — then apply it to an account or relationship to schedule the whole plan as real, tracked work.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <ModePill mode={mode} />
          <button className="btn btn--primary btn--sm" onClick={() => setDesigning("new")}><Icon name="plus" size={14} /> New playbook</button>
        </div>
      </div>

      <div className="stats">
        <Stat icon="route" label="Active playbooks" value={`${playbooks.filter((p) => p.enabled).length}`} sub={` / ${playbooks.length}`} foot="Enabled" />
        <Stat icon="bolt" label="Runs in flight" value={`${activeRuns.length}`} foot="Live across the portfolio" />
        <Stat icon="target" label="Avg success rate" value={`${playbooks.length ? Math.round(playbooks.reduce((s, p) => s + p.successRate, 0) / playbooks.length) : 0}`} sub="%" foot="Outcome reached" />
        <Stat icon="check" label="Completed runs" value={num(playbooks.reduce((s, p) => s + p.completedRuns, 0))} foot="All-time" />
      </div>

      {toast && <div className="toast"><Icon name="check" size={14} /> {toast}</div>}

      <div className="toolbar">
        {categories.map((c) => <button key={c} className={`chip ${category === c ? "chip--active" : ""}`} onClick={() => setCategory(c)}>{c === "all" ? "All" : c}</button>)}
      </div>

      <div className="grid-main">
        <div style={{ display: "grid", gap: 14 }}>
          {filtered.length === 0 ? <Empty>No playbooks in this category.</Empty> : filtered.map((p) => {
            const active = selected?.id === p.id;
            return (
              <div key={p.id} className="card" onClick={() => setSelectedId(p.id)} style={{ cursor: "pointer", borderColor: active ? "var(--corona)" : undefined, boxShadow: active ? "0 0 0 1px var(--corona), var(--shadow)" : undefined }}>
                <div className="card__bd">
                  <div className="row between" style={{ alignItems: "flex-start" }}>
                    <div className="row" style={{ gap: 11 }}>
                      <span className="n-ic" style={{ width: 34, height: 34, borderRadius: 9, display: "grid", placeItems: "center", background: "var(--surface-2)", border: "1px solid var(--line)" }}><Icon name="route" size={16} className="faint" /></span>
                      <div><b style={{ fontSize: 14.5 }}>{p.name}</b><small className="faint" style={{ display: "block", fontSize: 12.5 }}>{p.trigger}</small></div>
                    </div>
                    <span className="chip">{p.category}</span>
                  </div>
                  <div className="row between" style={{ marginTop: 14, marginBottom: 8 }}>
                    <span className="row" style={{ gap: 8 }}>
                      <button className="toggle" role="switch" aria-checked={p.enabled} onClick={(e) => { e.stopPropagation(); toggle(p); }}>
                        <span className={`toggle__track ${p.enabled ? "on" : ""}`}><span className="toggle__knob" /></span>
                      </button>
                      <span className="faint" style={{ fontSize: 12.5 }}>{p.enabled ? "Enabled" : "Paused"}</span>
                    </span>
                    <span className="row" style={{ gap: 14 }}>
                      <span className="faint" style={{ fontSize: 12.5 }}><b className="num" style={{ color: "var(--ink)" }}>{p.activeRuns}</b> active</span>
                      <span className="num" style={{ fontSize: 12.5, fontWeight: 600, color: rateColor(p.successRate) }}>{pct(p.successRate)}</span>
                    </span>
                  </div>
                  <Meter value={p.successRate} color={rateColor(p.successRate)} />
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          {selected ? (
            <div className="card">
              <div className="card__hd">
                <div><h3>{selected.name}</h3><span className="sub">{selected.trigger} · applies to {selected.scope ?? "account"} · ~{planDuration(selected.steps)}-day plan</span></div>
                <button className="btn btn--ghost btn--sm" onClick={() => setDesigning(selected)}><Icon name="settings" size={13} /> Design</button>
              </div>
              <div className="card__bd">
                <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>{selected.description}</p>
                <div className="launch-row">
                  <select className="inp" value={launchAcct} onChange={(e) => setLaunchAcct(e.target.value)}>{ws.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
                  <button className="btn btn--primary btn--sm" onClick={launch}><Icon name="bolt" size={14} /> Apply to account</button>
                </div>
                <p className="eyebrow" style={{ margin: "20px 0 12px" }}>Schedule preview · {selected.steps.filter((s) => isActionable(s.kind)).length} tasks</p>
                <Gantt run={previewRun(selected)} />
              </div>
            </div>
          ) : <div className="card"><div className="card__bd"><Empty>Select a playbook.</Empty></div></div>}
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div className="card__hd"><div><h3>Active runs</h3><span className="sub">Plays in flight — advance each step as it's done</span></div><span className="chip chip--active">{activeRuns.length}</span></div>
        <div className="card__bd">
          {activeRuns.length === 0 ? <Empty>No runs in flight. Launch a play above.</Empty> : (
            <div style={{ display: "grid", gap: 12 }}>
              {activeRuns.map((run) => {
                const done = run.steps.filter((s) => s.status === "done").length;
                const total = run.steps.filter((s) => s.status !== "skipped").length;
                return (
                  <div key={run.id} className="run">
                    <div className="row between" style={{ marginBottom: 10 }}>
                      <div><b style={{ fontSize: 13.5 }}>{run.playbookName}</b> <span className="faint" style={{ fontSize: 12.5 }}>· {run.accountName}</span></div>
                      <div className="row" style={{ gap: 8 }}>
                        <span className="faint" style={{ fontSize: 12 }}>{done}/{total} steps</span>
                        <button className="btn btn--sm" onClick={() => advance(run)} disabled={done >= total}><Icon name="check" size={13} /> Advance</button>
                        <button className="mini-btn" title="Stop run" onClick={() => stop(run)}><Icon name="dot" size={13} /></button>
                      </div>
                    </div>
                    <div className="run-steps">
                      {run.steps.filter((s) => s.status !== "skipped").map((s, i) => (
                        <span key={i} className={`run-step run-step--${s.status}`} title={s.label}>
                          <Icon name={s.status === "done" ? "check" : stepIcon[s.kind] ?? "dot"} size={12} /> {s.label}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {designing && <PlaybookDesigner ws={ws} initial={designing === "new" ? null : designing} onClose={() => setDesigning(null)} onSave={saveDesign} onDelete={designing !== "new" ? () => removePlaybook(designing) : undefined} />}
    </div>
  );
}

const blankStep = (i: number): PlaybookStep => ({ id: `s${i}_${Math.random().toString(36).slice(2, 6)}`, label: "", kind: "task", durationDays: 1, dependsOn: [] });

function PlaybookDesigner({ ws, initial, onClose, onSave, onDelete }: {
  ws: ReturnType<typeof useWorkspace>["ws"]; initial: Playbook | null;
  onClose: () => void; onSave: (input: Partial<Playbook> & { name: string }) => void; onDelete?: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [category, setCategory] = useState<Playbook["category"]>(initial?.category ?? "Onboarding");
  const [scope, setScope] = useState<PlaybookScope>(initial?.scope ?? "account");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [steps, setSteps] = useState<PlaybookStep[]>(initial ? initial.steps.map((s) => ({ ...s, dependsOn: s.dependsOn ?? [] })) : [blankStep(1)]);
  const valid = name.trim() && steps.every((s) => s.label.trim());

  const setStep = (i: number, patch: Partial<PlaybookStep>) => setSteps((ss) => ss.map((s, j) => j === i ? { ...s, ...patch } : s));
  const addStep = () => setSteps((ss) => [...ss, blankStep(ss.length + 1)]);
  const removeStep = (i: number) => setSteps((ss) => ss.filter((_, j) => j !== i).map((s) => ({ ...s, dependsOn: (s.dependsOn ?? []).filter((d) => d !== ss[i].id) })));

  const preview: Playbook = { id: "preview", name: name || "Untitled", category, description, trigger: "Manual", enabled: true, activeRuns: 0, completedRuns: 0, successRate: 0, scope, steps };

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900, width: "94vw" }}>
        <div className="compose-modal__hd">
          <div className="row" style={{ gap: 8 }}><Icon name="route" size={16} /><b>{initial ? `Design · ${initial.name}` : "New playbook"}</b></div>
          <button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button>
        </div>
        <div className="compose-modal__bd" style={{ display: "grid", gap: 16, maxHeight: "72vh", overflowY: "auto" }}>
          <div className="composer__grid">
            <label style={{ display: "grid", gap: 5 }}><span className="labeled__l">Name</span><input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enterprise onboarding" /></label>
            <label style={{ display: "grid", gap: 5 }}><span className="labeled__l">Category</span>
              <select className="inp" value={category} onChange={(e) => setCategory(e.target.value as Playbook["category"])}>{["Risk", "Onboarding", "Adoption", "Expansion", "Renewal"].map((c) => <option key={c} value={c}>{c}</option>)}</select>
            </label>
            <label style={{ display: "grid", gap: 5 }}><span className="labeled__l">Applies to</span>
              <select className="inp" value={scope} onChange={(e) => setScope(e.target.value as PlaybookScope)}><option value="account">Account</option><option value="relationship">Relationship</option><option value="both">Account or relationship</option></select>
            </label>
          </div>
          <label style={{ display: "grid", gap: 5 }}><span className="labeled__l">Description</span><input className="inp" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this play does" /></label>

          <div className="labeled__l">Steps &amp; dependencies</div>
          <div style={{ display: "grid", gap: 8 }}>
            {steps.map((s, i) => (
              <div key={s.id} className="card" style={{ boxShadow: "none" }}>
                <div className="card__bd" style={{ display: "grid", gap: 8 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="faint" style={{ fontFamily: "var(--mono)", fontSize: 12, width: 20 }}>{i + 1}</span>
                    <input className="inp" style={{ flex: 1 }} placeholder="Step label" value={s.label} onChange={(e) => setStep(i, { label: e.target.value })} />
                    <select className="inp" style={{ width: 110 }} value={s.kind} onChange={(e) => setStep(i, { kind: e.target.value as PlaybookStepKind })}>{STEP_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</select>
                    <button className="icon-btn" title="Remove step" onClick={() => removeStep(i)}><Icon name="dot" size={15} /></button>
                  </div>
                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                    <label className="row" style={{ gap: 5, fontSize: 12 }}><span className="faint">Duration</span><input className="inp inp--num" type="number" min={0} style={{ width: 58 }} value={s.durationDays ?? 1} onChange={(e) => setStep(i, { durationDays: Number(e.target.value) })} /><span className="faint">d</span></label>
                    <label className="row" style={{ gap: 5, fontSize: 12 }}><span className="faint">Lag</span><input className="inp inp--num" type="number" min={0} style={{ width: 52 }} value={s.offsetDays ?? 0} onChange={(e) => setStep(i, { offsetDays: Number(e.target.value) })} /><span className="faint">d</span></label>
                    <label className="row" style={{ gap: 5, fontSize: 12 }}><span className="faint">Type</span>
                      <select className="inp inp--sm" value={s.taskTypeId ?? ""} onChange={(e) => setStep(i, { taskTypeId: e.target.value || undefined })}><option value="">None</option>{ws.taskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
                    </label>
                    <label className="row" style={{ gap: 5, fontSize: 12 }}><span className="faint">Owner</span>
                      <select className="inp inp--sm" value={s.assignee ? (s.assignee.kind === "role" ? `role:${s.assignee.role}` : `user:${s.assignee.userId}`) : "role:csm"} onChange={(e) => { const [k, id] = e.target.value.split(":"); setStep(i, { assignee: k === "role" ? { kind: "role", role: id } as Assignee : { kind: "user", userId: id } as Assignee }); }}>
                        <optgroup label="Roles">{ws.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.label}</option>)}</optgroup>
                        <optgroup label="People">{ws.users.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>
                      </select>
                    </label>
                  </div>
                  {i > 0 && (
                    <div className="row" style={{ gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <span className="faint" style={{ fontSize: 12 }}>Depends on:</span>
                      {steps.slice(0, i).map((dep) => {
                        const on = (s.dependsOn ?? []).includes(dep.id);
                        return <button key={dep.id} className={`chip ${on ? "chip--active" : ""}`} style={{ fontSize: 11.5 }} onClick={() => setStep(i, { dependsOn: on ? (s.dependsOn ?? []).filter((d) => d !== dep.id) : [...(s.dependsOn ?? []), dep.id] })}>{dep.label || `Step ${steps.indexOf(dep) + 1}`}</button>;
                      })}
                      {i === 0 && <span className="faint" style={{ fontSize: 12 }}>none</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button className="btn btn--ghost btn--sm" onClick={addStep} style={{ justifySelf: "start" }}><Icon name="plus" size={13} /> Add step</button>
          </div>

          <div>
            <div className="labeled__l" style={{ marginBottom: 8 }}>Live schedule · {planDuration(steps)} days</div>
            {steps.some((s) => s.label.trim()) ? <Gantt run={previewRun(preview)} /> : <div className="faint" style={{ fontSize: 12.5 }}>Add steps to preview the schedule.</div>}
          </div>
        </div>
        <div className="compose-modal__hd" style={{ borderTop: "1px solid var(--line-soft)", borderBottom: "none", justifyContent: "space-between" }}>
          <span>{onDelete && <button className="btn btn--ghost btn--sm" onClick={onDelete}>Delete</button>}</span>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn--sm" onClick={onClose}>Cancel</button>
            <button className="btn btn--primary btn--sm" disabled={!valid} onClick={() => onSave({ name: name.trim(), category, scope, description: description.trim(), steps })}>{initial ? "Save changes" : "Create playbook"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub, foot }: { icon: string; label: string; value: string; sub?: string; foot: string }) {
  return (
    <div className="stat">
      <div className="stat__label"><Icon name={icon} size={14} /> {label}</div>
      <div className="stat__value">{value}{sub && <small>{sub}</small>}</div>
      <div className="stat__foot">{foot}</div>
    </div>
  );
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}><i />{mode === "api" ? "Live API" : "Local demo"}</span>;
}
